import { Code } from '../types';
import ParameterKeyValue from './ParameterKeyValue';

/*
  these types are not conclusive, feel free to add to them.  This is just a starting point
  to prevent dumb typos
*/
export type RangeCondition = {
  gte?: string;
  lte?: string;
  format: 'strict_date_optional_time';
};

export type RangeClause = {
  range: Record<string, RangeCondition>;
};

export type TermClause = {
  term: Record<string, string>;
};

export type ShouldClause = {
  should: AssertionClause[];
};

export type MustClause = {
  must: AssertionClause[];
};

export type MustNotClause = {
  must_not: AssertionClause[];
};

export type BoolClause = {
  bool: MustClause | ShouldClause | MustNotClause;
};

export type AssertionClause = RangeClause | TermClause | BoolClause;

export type CodingWithTerminologyId = fhir.Coding & { terminologyId: string };

function isTerminologyCoding(
  coding: fhir.Coding | CodingWithTerminologyId,
): coding is CodingWithTerminologyId {
  return (coding as CodingWithTerminologyId).terminologyId !== undefined;
}

export default abstract class Parameter {
  _getFSSPatientReferenceClause = (
    docProp: string,
    patientId: string,
  ): BoolClause => ({
    bool: {
      should: [`Patient/${patientId}`, patientId].map<TermClause>(_ => ({
        term: { [docProp]: _ },
      })),
    },
  });

  _getFSSCodeableConceptClause = (
    codeableConceptBaseDocProp: string,
    coding: fhir.Coding | CodingWithTerminologyId,
  ): BoolClause => {
    const getCodingProp = (prop: string) =>
      `${codeableConceptBaseDocProp}.coding.${prop}.keyword`;
    const standardCodingSearchConditions = [
      { prop: getCodingProp('system'), value: coding?.system || null },
      {
        prop: getCodingProp('display'),
        value: (coding as fhir.Coding)?.display || null,
      },
      {
        prop: getCodingProp('code'),
        value: (coding as fhir.Coding)?.code || (coding as Code)?.key || null,
      },
    ].filter(_ => _.value !== null);

    const baseCodingClause: BoolClause = {
      bool: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        must: standardCodingSearchConditions.map(_ => ({
          term: {
            [_.prop]: _.value,
          },
        })),
      },
    };

    // if the coding has a terminologyId, search like a standard coding but also the terminologyId for child concepts
    if (isTerminologyCoding(coding)) {
      return {
        bool: {
          should: [
            baseCodingClause,
            {
              bool: {
                must: [
                  {
                    term: {
                      [`${codeableConceptBaseDocProp}.coding.extension.valueString.keyword`]:
                        coding.terminologyId,
                    },
                  },
                ],
              },
            },
          ],
        },
      };
    } else {
      // just return the standard coding clause - standard code search
      return baseCodingClause;
    }
  };

  abstract get parameterKeyValue(): ParameterKeyValue;

  abstract getFSSClause(): Promise<BoolClause | null>;
}
